import { useState, useCallback, ReactElement, useRef } from 'react';

import { ChipsContainer, CustomChip, DataProvider, Input, VSpacing, SuggestProps } from '@hh.ru/magritte-ui';

import FilterWrapper from 'src/components/Search/Common/Filters/FilterWrapper';
import { useApplyFilter } from 'src/components/Search/Common/Filters/hooks/useApplyFilter';
import SuggestWithDefaultErrorPlaceholder from 'src/components/SuggestWithDefaultErrorPlaceholder';
import { ClusterKey } from 'src/types/search/common/clusters';
import { AutosuggestWithText } from 'src/utils/suggest/buildDataProvider';

export type FilterSuggestItem = AutosuggestWithText & { id: number };

interface FilterWithSuggestProps<T extends FilterSuggestItem> {
    title: string;
    name: typeof ClusterKey.Citizenship | typeof ClusterKey.University | typeof ClusterKey.WorkTicket;
    selectedValues: Array<T>;
    dataProvider: DataProvider<T>;
    placeholder?: string;
    normalizeSelectedItem?: (item: T) => T;
    renderItem?: SuggestProps<T>['renderItem'];
}

const FilterWithSuggest = <T extends FilterSuggestItem>({
    title,
    selectedValues,
    name,
    dataProvider,
    placeholder,
    normalizeSelectedItem,
    renderItem,
}: FilterWithSuggestProps<T>): ReactElement => {
    const suggestHostRef = useRef(null);

    const applyFilter = useApplyFilter(name);

    const [inputValue, setInputValue] = useState('');

    const handleSelect = useCallback(
        (_: string, item?: T) => {
            if (!item?.id) {
                return true;
            }
            const itemNormalized = normalizeSelectedItem?.(item) || item;
            if (!selectedValues.some(({ id }) => id === itemNormalized.id)) {
                setTimeout(() => setInputValue(''));
                applyFilter([...selectedValues, itemNormalized].map(({ id }) => id));
            }
            return true;
        },
        [applyFilter, normalizeSelectedItem, selectedValues]
    );

    const handleRemove = useCallback(
        (removableItemId: number) =>
            applyFilter(selectedValues.map(({ id }) => id).filter((id) => id !== removableItemId)),
        [applyFilter, selectedValues]
    );

    return (
        <FilterWrapper filterKey={name} title={title}>
            <div style={{ position: 'relative' }} ref={suggestHostRef}>
                <SuggestWithDefaultErrorPlaceholder
                    navigationBarProps={{ title }}
                    dataProvider={dataProvider}
                    onSelectValidator={handleSelect}
                    inputValue={inputValue}
                    input={{
                        component: Input,
                        props: {
                            value: inputValue,
                            onChange: setInputValue,
                            placeholder,
                            'data-qa': `novafilter-item-input__${name}`,
                        },
                    }}
                    renderItem={renderItem}
                />
            </div>
            <VSpacing default={12} />
            <ChipsContainer>
                {selectedValues.map(({ id, text }) => (
                    <CustomChip key={id} Element="button" onDelete={() => handleRemove(id)} aria-label={text}>
                        {text}
                    </CustomChip>
                ))}
            </ChipsContainer>
        </FilterWrapper>
    );
};

export default FilterWithSuggest;
